import { el } from '../ui.js';
import { MARK } from './landing.js';

/**
 * The Legal page.
 *
 * WHY THIS IS WRITTEN THE WAY IT IS
 * Everything below describes what the software actually does today, checked
 * against the code rather than against a template. Where the honest answer is
 * unflattering — one person can read any agency's data; there is no uptime
 * promise — it says so. A privacy policy is a statement other people rely on,
 * and several states treat a published one as enforceable against the company
 * that published it. Boilerplate that overstates the position is not a
 * placeholder, it is a misrepresentation with a date on it.
 *
 * The specific claims here and where they come from:
 *   third parties     grep of every outbound host in apps/api/src
 *   AI is opt-in      DEFAULT_CLAUDE.apiKey is null; org settings must enable it
 *   AI redaction      sanitize() and scrubText() in services/claude.ts
 *   agency isolation  Postgres row-level security, test 'IT NEVER CROSSES AGENCIES'
 *   operator access   sole operator holds the database credential. No technical
 *                     barrier exists. Saying otherwise would be false.
 */

const YEAR = new Date().getFullYear();

interface Clause { h: string; body: string[] }
interface Doc { id: string; title: string; intro: string; clauses: Clause[] }

const DOCS: readonly Doc[] = [
  {
    id: 'terms',
    title: 'Terms of service',
    intro: 'What an agency agrees to by using Cellvora, and what Cellvora is on the hook for.',
    clauses: [
      {
        h: 'What you get',
        body: [
          'A right to use Cellvora for your own insurance business, for as long as your '
          + 'arrangement with CellVora LLC continues. Not ownership, not a copy of the source, '
          + 'and not a right to resell it, host it, or build something from its design.',
        ],
      },
      {
        h: 'What it costs',
        body: [
          'Whatever is agreed with you in writing. Where an agency is in a pilot, CellVora '
          + 'carries the cost of building and running the platform while it is being proven, '
          + 'and the agency later takes over the third-party cost of running it — at cost, with '
          + 'invoices shown on request. Any other arrangement takes a separate signed agreement.',
        ],
      },
      {
        h: 'No promise of uptime',
        body: [
          'There is no service level agreement. Cellvora is run by one person on third-party '
          + 'infrastructure, and it can be unavailable, slow, or interrupted for maintenance '
          + 'without notice. Reasonable effort is made to keep it running and to restore it '
          + 'quickly. That is a commitment of effort, not of availability, and no agency should '
          + 'build a process around this platform that fails badly if it is down for a day.',
          'Keep your own copy of anything you cannot afford to lose. An export is always '
          + 'available to you.',
        ],
      },
      {
        h: 'What Cellvora is responsible for',
        body: [
          'CellVora LLC’s total liability to an agency is limited to the greater of one thousand '
          + 'United States dollars (US$1,000) and the amount that agency paid CellVora in the '
          + 'twelve months before the claim arose. Neither party is liable to the other for '
          + 'indirect or consequential loss, lost commissions, lost business or lost goodwill.',
          'That limit does not apply to fraud, gross negligence or willful misconduct, or to '
          + 'CellVora’s obligations of confidentiality and security over your data.',
        ],
      },
      {
        h: 'What remains yours to get right',
        body: [
          'Licensing, carrier appointments, consent, do-not-call obligations, calling hours, '
          + 'recording disclosures, suitability and record-keeping. Cellvora enforces rules it '
          + 'is given and records what happened; it does not hold your licences and it is not '
          + 'your compliance officer.',
          'Premiums shown in the platform come from rate cards your agency loads. Only the '
          + 'carrier can quote a binding rate.',
        ],
      },
      {
        h: 'Ending it',
        body: [
          'Either side can end the arrangement at any time by written notice. On termination '
          + 'you get an export of your data and your access stops. Access may be suspended '
          + 'immediately where there is a reasonable belief the platform is being used '
          + 'unlawfully.',
        ],
      },
      {
        h: 'Law and courts',
        body: [
          'Michigan law. The state courts sitting in Oakland County, Michigan and the United '
          + 'States District Court for the Eastern District of Michigan. No arbitration clause — '
          + 'disputes go to a court of record, which is the same forum named in every other '
          + 'Cellvora agreement.',
        ],
      },
    ],
  },

  {
    id: 'privacy',
    title: 'Privacy',
    intro: 'Cellvora holds personal information about two different groups of people, and the '
      + 'answers are different for each. Agency staff have accounts here. Consumers do not — '
      + 'their details are in the platform because an agency put them there.',
    clauses: [
      {
        h: 'Who is responsible for consumer data',
        body: [
          'The agency, not Cellvora. An agency decides which consumers go into the platform, '
          + 'why, on what lawful basis, and how long they stay. Cellvora holds and processes '
          + 'that information on the agency’s instructions and for no purpose of its own.',
          'So if a consumer asks what is held about them, asks for a copy, or asks to be '
          + 'deleted, the agency answers. Cellvora will help the agency answer and will act on '
          + 'the agency’s instruction, but it will not act on a consumer request behind the '
          + 'agency’s back — it is not Cellvora’s data to give away or to erase.',
        ],
      },
      {
        h: 'One person can read your data, and you should know that',
        body: [
          'Agencies are separated from each other by Postgres row-level security, enforced by '
          + 'the database rather than by application code, and tested. No screen, export or API '
          + 'call hands one agency another agency’s records.',
          'That boundary does not apply to the operator. CellVora LLC has a sole operator who '
          + 'holds the production database credential, and there is no technical barrier '
          + 'preventing him from reading any record in it. He does so only to diagnose a fault '
          + 'or to answer a request for help. Claiming that nobody at Cellvora can see your data '
          + 'would be untrue, and a policy that says something untrue is worse than one that '
          + 'admits an uncomfortable fact.',
          'This will change as the company grows, and this page will change with it.',
        ],
      },
      {
        h: 'Who else touches it',
        body: [
          'Neon — the database, hosted on Amazon Web Services in the United States (us-east-2). '
          + 'Every record lives here.',
          'Netlify — hosting and delivery of the application itself.',
          'A text-message provider, only if an agency turns on sign-in codes by SMS, and only '
          + 'ever the mobile number of an agency user. Consumer numbers are never texted by the '
          + 'platform.',
          'An AI provider, only if an agency turns the assistant on. It is off unless switched '
          + 'on deliberately — no key is configured by default. See the next paragraph, which '
          + 'is the part that matters.',
          'That is the whole list. There is no analytics service, no advertising network, no '
          + 'data broker, and no payment processor. Nothing about you or your clients is sold, '
          + 'rented or shared for anybody’s marketing.',
        ],
      },
      {
        h: 'What the AI assistant is and is not sent',
        body: [
          'When an agency enables it, personal details are stripped before anything leaves the '
          + 'server. Names, phone numbers, alternative numbers, email addresses, street '
          + 'addresses, ZIP codes, IP addresses and consent records are removed from the record '
          + 'first, and the operator’s own typed question is scanned and scrubbed as well — a '
          + 'free-text box is exactly where somebody pastes a phone number.',
          'The assistant is told, every time, that it may not authorise a call, override a '
          + 'compliance block, claim a policy is approved or a rate is bound, or give legal '
          + 'advice about telemarketing rules.',
        ],
      },
      {
        h: 'Improving the platform',
        body: [
          'Counts, timings and error rates may be used to make the software better. Never names, '
          + 'contact details or policy details, and never in a form that identifies an agency, '
          + 'an agent or a consumer. Your book is not training data and it is not a product.',
        ],
      },
      {
        h: 'How long it is kept',
        body: [
          'While you are using the platform, for as long as you want it. An export is available '
          + 'to you at any time and within thirty days of a request.',
          'After an agency leaves, its data is retained for ninety days so that it can be '
          + 'recovered if leaving was a mistake, and is then deleted. Backups roll off on their '
          + 'own cycle after that. The audit trail of what happened may be kept longer where a '
          + 'law or a carrier requires a record to exist.',
        ],
      },
      {
        h: 'Accounts',
        body: [
          'For agency staff, Cellvora holds a name, work email, optional mobile number for '
          + 'sign-in codes, state licences, role, and a log of what was done in the system. That '
          + 'is what running a multi-user platform with an audit trail requires, and it is not '
          + 'used for anything else.',
        ],
      },
    ],
  },

  {
    id: 'security',
    title: 'Security',
    intro: 'What is done to keep this data safe, and what happens when something goes wrong.',
    clauses: [
      {
        h: 'Separation between agencies',
        body: [
          'Enforced by Postgres row-level security — by the database itself, not by application '
          + 'code that could be bypassed by a missing filter in one query. There is a test named '
          + 'for exactly this, and it fails the build if the boundary ever leaks.',
        ],
      },
      {
        h: 'Encryption and secrets',
        body: [
          'Data is encrypted in transit and at rest. Multi-factor secrets are sealed with '
          + 'AES-256-GCM and the system refuses to store one rather than store it unsealed. '
          + 'One-time codes are held as keyed hashes, never as the code itself.',
        ],
      },
      {
        h: 'A record that cannot be quietly altered',
        body: [
          'Consequential actions are written to an audit trail in which each entry is '
          + 'cryptographically chained to the one before it. Changing a past entry breaks the '
          + 'chain, and the system can prove it was broken.',
        ],
      },
      {
        h: 'Who can reach production',
        body: [
          'One person: CellVora LLC’s sole operator. Nobody else has the credential, and there '
          + 'are no employees, contractors or agencies with production access. When that '
          + 'changes, this page changes first.',
        ],
      },
      {
        h: 'If there is a breach',
        body: [
          'Every affected agency will be told within seventy-two hours of Cellvora becoming '
          + 'aware of it — what happened, what data was involved, what is known and what is not '
          + 'yet known, and what is being done. Notice will not be delayed to finish an '
          + 'investigation first; the second message will correct the first if it has to.',
          'Notifying consumers under state breach-notification law is the agency’s '
          + 'responsibility, since the agency holds the relationship with them. Cellvora will '
          + 'give the agency everything it needs to do that.',
        ],
      },
    ],
  },

  {
    id: 'acceptable-use',
    title: 'Acceptable use',
    intro: 'Calling rules and licensing are the agency’s responsibility. These are the lines '
      + 'CellVora enforces on its own account, and crossing one ends access.',
    clauses: [
      {
        h: 'Not permitted',
        body: [
          'Calling, texting or emailing anyone the platform’s compliance gate has blocked, or '
          + 'working around that gate by any means.',
          'Loading a lead list the agency has no lawful right to use, or warranting a consent '
          + 'that was never given.',
          'Using the platform without the licences and carrier appointments the business '
          + 'requires.',
          'Quoting a premium as binding, or presenting a figure from an unverified rate card as '
          + 'though a carrier had confirmed it.',
          'Sharing an account. Every person who uses Cellvora gets their own login, because an '
          + 'audit trail that cannot say who did something is not an audit trail.',
          'Attempting to reach another agency’s data, probe the platform’s security, or copy the '
          + 'software or its design.',
        ],
      },
      {
        h: 'What happens',
        body: [
          'Access can be suspended immediately where there is a reasonable belief any of the '
          + 'above is happening. The agency will be told what was seen and why. Nothing here '
          + 'requires Cellvora to police an agency’s calling, and none of it shifts '
          + 'responsibility for that calling away from the agency.',
        ],
      },
    ],
  },
];

export function legalView(go: (p: string) => void): HTMLElement {
  const page = el('div', { class: 'lp lp-doc' });

  const home = el('a', { class: 'lp-brand', href: '#/', 'aria-label': 'Cellvora' },
    el('span', { class: 'lp-mark', html: MARK }),
    el('span', { class: 'lp-word', text: 'CELLVORA' }));
  const back = el('button', { class: 'btn', type: 'button', text: 'Back' });
  back.addEventListener('click', () => go('/'));
  page.append(el('header', { class: 'lp-top' }, home, back));

  page.append(
    el('section', { class: 'lp-enter lp-docHead' },
      el('h1', { class: 'lp-h1', text: 'Legal' }),
      el('p', { class: 'lp-line' },
        'The terms Cellvora operates under, and what it does with the information an agency '
        + 'puts into it. Written to describe what the software actually does — including the '
        + 'parts that are not flattering.'),
      el('p', { class: 'lp-docScope', text: `Last updated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}. These terms have not been reviewed by an attorney.` })),
  );

  /* ------------------------------------------------------------ ownership -- */
  /*
   * Final, and first, on purpose.
   *
   * A published, dated, public claim of authorship is ordinary evidence: it is
   * visible to anyone, archived by the web without anybody being asked, and it
   * exists from the day the page ships rather than from the day a dispute
   * starts. It says nothing the executed IP assignment does not — the value is
   * that this version is public.
   *
   * The wording claims founding, design and ownership, all true and all
   * documented, and stops there. It does not claim invention of anything
   * patentable, or that no tool was used to write any line. A notice that
   * overreaches is the one an opponent attacks first, and the attack lands on
   * the whole notice rather than on the sentence that deserved it.
   */
  page.append(
    el('section', { class: 'lp-section' },
      el('h2', { class: 'lp-h2', text: 'Ownership' }),
      el('p', null,
        'Cellvora was founded, conceived and designed by ',
        el('b', { text: 'Muhammad Alzubaidi' }),
        ', and is owned by ',
        el('b', { text: 'CellVora LLC' }),
        ', a Michigan limited liability company of which he is the sole member.'),
      el('p', null,
        'That ownership covers the whole of it: the source code, the database design, the '
        + 'compliance gate, the lead scoring model, the draft-date engine, the quoting engine, '
        + 'the audit-trail design, the interface, the documentation, the name Cellvora, and the '
        + 'domain cellvora.ai — together with every copyright, trade secret, trademark and '
        + 'patent right in any of them.'),
      el('p', null,
        'No part of it is licensed, assigned or made available for reuse. Agencies using the '
        + 'platform receive a right to use it and nothing further: no ownership, no source, and '
        + 'no right to copy, reproduce or build from its design. Ideas, feedback and feature '
        + 'requests contributed by anyone using it belong to CellVora LLC.'),
      el('p', { class: 'lp-docScope' },
        `© ${YEAR} CellVora LLC. All rights reserved. Cellvora is a trademark of CellVora LLC.`)),
  );

  /* ----------------------------------------------------------- the four -- */
  for (const doc of DOCS) {
    page.append(
      el('section', { class: 'lp-section lp-docSection', id: doc.id },
        el('h2', { class: 'lp-h2', text: doc.title }),
        el('p', { class: 'lp-docScope', text: doc.intro }),
        ...doc.clauses.map((c) => el('div', { class: 'lp-clause' },
          el('h3', { class: 'lp-h3', text: c.h }),
          ...c.body.map((b) => el('p', { text: b })))),
      ),
    );
  }

  /* ------------------------------------------------------ what it is not -- */
  page.append(
    el('section', { class: 'lp-band' },
      el('h2', { class: 'lp-h2', text: 'What Cellvora is not' }),
      el('p', null,
        'Cellvora is agency software. It is not an insurer. It is not an insurance agency. It '
        + 'does not sell, broker or underwrite any policy, and it is not a source of leads.'),
      el('p', null,
        'It gives no legal, compliance, tax or financial advice. Licensing, appointments, '
        + 'calling rules, consent, suitability and record-keeping remain the responsibility of '
        + 'the agency using it and of the licensed people who work there.'),
      el('p', null,
        'Features described anywhere on this site as in development are not available today, '
        + 'and no date is promised for them.')),
  );

  page.append(
    el('footer', { class: 'lp-foot' },
      el('span', { html: MARK, class: 'lp-footmark' }),
      el('p', { class: 'lp-copy', text: `© ${YEAR} CellVora LLC` })),
  );

  return page;
}
